/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

import type { Box } from "@bfly/engine";
import { clamp01 } from "@bfly/engine/math";

// How far the carve's top arch is raised, as a fraction of the arch's height (its radius).
// The field's shader and the wisps' carve test (field.ts) both read it.
export const ARCH_LIFT = 0.25;
// With a taper element (field.ts), the bottom of the carve narrows like the narrow end of an
// egg. The element's `--cap-height` is its height in half-widths of the carve, and
// `--cap-drop` is how far its tip sits below the carve's bottom. The page sets both (on the
// footer, in site.scss) and makes room for it. The sides close in faster than an ellipse's:
// the half-width at depth t (0 at the top, 1 at the tip) is an ellipse's times
// `1 - CAP_TAPER * t`.
export const CAP_TAPER = 0.35;
// The dithered band is at most this many times the field's side gap. On a narrow screen the
// gap is thinner than the band, so the glyphs there would barely show. A band twice the gap
// reaches about half density at the screen's edge. Desktop is unaffected.
const BAND_GAP_SHARE = 2;

// An approximate signed distance to that egg end, negative inside (Inigo Quilez's ellipse
// estimate, with x widened by the taper). Same math as the shader's `capDistance()`. The egg
// is `halfWidth` wide on each side of `centerX`, from `capTop` to `capBottom`.
export function capDistance(
  x: number,
  y: number,
  centerX: number,
  halfWidth: number,
  capTop: number,
  capBottom: number,
): number {
  const height = capBottom - capTop;
  const t = clamp01((y - capTop) / height);
  const qx = Math.abs(x - centerX) / (1 - CAP_TAPER * t);
  const qy = y - capTop;
  const k0 = Math.hypot(qx / halfWidth, qy / height);
  const k1 = Math.max(Math.hypot(qx / (halfWidth * halfWidth), qy / (height * height)), 1e-6);
  return (k0 * (k0 - 1)) / k1;
}

/** The carve in viewport CSS pixels: its column, the top and tip of its egg end (equal
    when it has no egg end), and the padded breakouts. */
export interface CarveShape {
  column: Box;
  capTop: number;
  capBottom: number;
  breakouts: readonly Box[];
}

/** The egg end's top and tip below `column`, from the taper element's `--cap-height` and
    `--cap-drop`. Without an egg end, the top equals the tip, which reads as no egg end. */
export function capEnds(
  column: Box,
  cap: { height: number; drop: number } | null,
): { top: number; bottom: number } {
  if (!cap) return { top: column.bottom, bottom: column.bottom };
  const bottom = column.bottom + cap.drop;
  return { top: bottom - cap.height * ((column.right - column.left) / 2), bottom };
}

/** How far a point is outside the carve and every breakout, in CSS pixels (0 inside). The
    shader's `carveEdge()` does the same, then divides by the band's width. */
export function carveOutside(x: number, y: number, shape: CarveShape): number {
  const { column, capTop, capBottom } = shape;
  const radius = (column.right - column.left) / 2;
  const archX = column.left + radius;
  const archY = column.top + radius * (1 - ARCH_LIFT);
  // The box runs from the arch's center down to the egg end's top.
  const outsideX = Math.max(column.left - x, 0, x - column.right);
  const outsideY = Math.max(archY - y, 0, y - capTop);
  const outsideArch = Math.max(Math.hypot(x - archX, y - archY) - radius, 0);
  let outside = Math.min(Math.hypot(outsideX, outsideY), outsideArch);
  if (capBottom > capTop)
    outside = Math.min(outside, Math.max(capDistance(x, y, archX, radius, capTop, capBottom), 0));
  for (const b of shape.breakouts) {
    const bx = Math.max(b.left - x, 0, x - b.right);
    const by = Math.max(b.top - y, 0, y - b.bottom);
    outside = Math.min(outside, Math.hypot(bx, by));
  }
  return outside;
}

/** The dithered band's width in CSS pixels: `widest`, or `BAND_GAP_SHARE` times the
    narrowest side gap beside the column or a breakout, whichever is less. At least `least`,
    so a zero gap gives a hard edge instead of a division by zero. */
export function bandWidth(
  shape: CarveShape,
  viewportWidth: number,
  widest: number,
  least: number,
): number {
  let gap = Math.min(shape.column.left, viewportWidth - shape.column.right);
  for (const b of shape.breakouts) gap = Math.min(gap, b.left, viewportWidth - b.right);
  return Math.max(Math.min(widest, BAND_GAP_SHARE * gap), least);
}
